import { useState, useEffect } from "react";
import { Star, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (r, approved) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { approved });
      toast.success(approved ? "Review published" : "Review hidden");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleFeatured = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { featured: !r.featured });
      toast.success(r.featured ? "Removed from featured" : "Featured — shows first");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!reviews) return <div className="h-40 bg-muted animate-pulse" />;

  const filtered = reviews.filter((r) => {
    if (filter === "live" && !r.approved) return false;
    if (filter === "hidden" && r.approved) return false;
    if (filter === "featured" && !r.featured) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.name.toLowerCase().includes(q) || (r.product_name || "").toLowerCase().includes(q) || r.comment.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div data-testid="admin-reviews">
      <h1 className="font-serif text-3xl font-semibold mb-6">Reviews <span className="text-sm text-muted-foreground font-sans font-normal">({filtered.length} of {reviews.length})</span></h1>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["all", "All"], ["live", "Live"], ["hidden", "Hidden"], ["featured", "Featured"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors duration-300 ${filter === v ? "bg-[#111] text-[#FAF9F6] border-[#111]" : "border-black/15 hover:border-gold"}`}
            data-testid={`review-filter-${v}`}>
            {l}
          </button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, product, text..."
          className="ml-auto border border-black/15 bg-white px-3 py-2 text-sm w-56 outline-none focus:border-gold" data-testid="review-search" />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-reviews">No reviews match.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((r) => (
            <div key={r.id} className="bg-white border border-black/10 p-4" data-testid={`review-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={14} strokeWidth={1.5} className={i <= r.rating ? "fill-gold text-gold" : "text-black/20"} />
                  ))}
                </div>
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground">on {r.product_name}</p>
                <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-IN")}</p>
                <div className="flex-1" />
                <button onClick={() => toggleFeatured(r)} className="p-2 hover:text-gold transition-colors duration-300" data-testid={`review-feature-${r.id}`} aria-label="Feature" title="Feature — shows first">
                  <Star size={16} strokeWidth={1.5} className={r.featured ? "fill-gold text-gold" : "text-muted-foreground"} />
                </button>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch checked={r.approved} onCheckedChange={(v) => toggle(r, v)} data-testid={`review-toggle-${r.id}`} />
                  {r.approved ? "Live" : "Hidden"}
                </label>
                <button onClick={() => remove(r)} className="p-2 hover:text-destructive transition-colors duration-300" data-testid={`review-delete-${r.id}`} aria-label="Delete">
                  <Trash2 strokeWidth={1.5} size={16} />
                </button>
              </div>
              {r.title && <p className="text-sm font-medium mt-2">{r.title}</p>}
              <p className="text-sm text-muted-foreground mt-1">{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
